/* Trustpilot's own widgets, fetched late. The markup on the page is Trustpilot's code as supplied
   (snippets/trustpilot-widget.liquid). This file only decides when Trustpilot's script is fetched: not before a
   widget is near the screen, not before the page itself has loaded, and only where the visitor's region asks for
   no consent or consent was given. Until then, and if the script is blocked, the widget's own text link to the
   profile stays in its place. */
const SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const SEL = '.trustpilot-widget';
let state = 'idle'; // idle, loading, ready, failed

const widgets = (root = document) => Array.from(root.querySelectorAll(SEL));

// The one-line widget (Micro Combo) shows its two-line phone layout where it has less than 400px of room (see the
// widths in snippets/trustpilot-widget.liquid). Its frame height is its own setting: the height from the code for
// one line, 46px for two. Set before Trustpilot's script reads it, and again when the room changes (a phone turned
// on its side).
const oneLine = new WeakMap();
function fit(el) {
  const box = el.closest('.tpw-combo');
  if (!box || !box.clientWidth) return false;
  if (!oneLine.has(el)) oneLine.set(el, el.getAttribute('data-style-height') || '20px');
  const want = box.clientWidth < 400 ? '46px' : oneLine.get(el);
  if (el.getAttribute('data-style-height') === want) return false;
  el.setAttribute('data-style-height', want);
  return true;
}
const drawn = (el) => !!el.querySelector('iframe');
const draw = (el) => { if (window.Trustpilot && window.Trustpilot.loadFromElement) window.Trustpilot.loadFromElement(el, true); };

function fetchScript() {
  if (state !== 'idle') return;
  state = 'loading';
  const s = document.createElement('script');
  s.src = SRC;
  s.async = true;
  // Trustpilot's script draws the widgets it finds. Any it has not drawn a moment later are drawn here.
  s.onload = () => { state = 'ready'; setTimeout(() => widgets().filter((el) => !drawn(el)).forEach(draw), 800); };
  s.onerror = () => { state = 'failed'; };
  document.head.appendChild(s);
}

function afterLoad(fn) {
  const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

function watch() {
  const list = widgets();
  if (!list.length || state !== 'idle') return;
  list.forEach(fit);
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(() => widgets().forEach((el) => { if (fit(el) && state === 'ready') draw(el); }));
    list.forEach((el) => { const box = el.closest('.tpw-combo'); if (box) ro.observe(box); });
  }
  if (!('IntersectionObserver' in window)) { afterLoad(fetchScript); return; }
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return;
    io.disconnect();
    afterLoad(fetchScript);
  }, { rootMargin: '400px 0px' });
  list.forEach((el) => io.observe(el));
}

function privacy() {
  return new Promise((resolve) => {
    const S = window.Shopify;
    if (S && S.customerPrivacy) { resolve(S.customerPrivacy); return; }
    if (!S || !S.loadFeatures) { resolve(null); return; }
    S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => resolve(error ? null : window.Shopify.customerPrivacy));
  });
}

async function start() {
  if (!widgets().length) return;
  const p = await privacy();
  const allowed = () => !p || typeof p.analyticsProcessingAllowed !== 'function' || p.analyticsProcessingAllowed();
  if (allowed()) { watch(); return; }
  document.addEventListener('visitorConsentCollected', () => { if (allowed()) watch(); });
}

// The theme editor redraws a section when its settings change: draw the widgets inside it again.
document.addEventListener('shopify:section:load', (e) => {
  const list = widgets(e.target);
  if (!list.length) return;
  list.forEach(fit);
  if (state === 'ready') list.forEach(draw);
  else fetchScript();
});

start();
